import { z } from 'zod';
import { EventSource, EventStatus, EventType, Severity } from './enums.js';
/** A classified, persisted occurrence. Produced only by the event processor. */
export const VisionEvent = z.object({
    id: z.string().uuid(),
    eventType: EventType,
    severity: Severity,
    status: EventStatus,
    source: EventSource,
    cameraId: z.string().uuid().nullable(),
    cameraName: z.string().nullable().optional(),
    zoneId: z.string().uuid().nullable(),
    zoneName: z.string().nullable().optional(),
    confidence: z.number().min(0).max(1).nullable(),
    occurredAt: z.string().datetime(),
    correlationId: z.string(),
    /** Free-form, event-type specific. Never contains identity information. */
    metadata: z.record(z.unknown()).default({}),
    snapshotPath: z.string().nullable().default(null),
    createdAt: z.string().datetime(),
});
export const EventQuery = z.object({
    cameraId: z.string().uuid().optional(),
    zoneId: z.string().uuid().optional(),
    eventType: EventType.optional(),
    severity: Severity.optional(),
    since: z.string().datetime().optional(),
    until: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
    cursor: z.string().optional(),
});
/**
 * Human-readable one-liners. Kept beside the enum so the dashboard, the email
 * body and the WhatsApp template never drift apart.
 */
export const EVENT_LABELS = {
    PERSON_ENTERED: 'Person entered',
    PERSON_EXITED: 'Person exited',
    ZONE_OCCUPANCY_CHANGED: 'Zone occupancy changed',
    LINE_CROSSED: 'Line crossed',
    RESTRICTED_AREA_ENTRY: 'Restricted area entry',
    AFTER_HOURS_MOVEMENT: 'After-hours movement',
    CROWD_THRESHOLD: 'Crowd threshold exceeded',
    QUEUE_THRESHOLD: 'Queue threshold exceeded',
    STORAGE_ACTIVITY: 'Storage activity',
    CAMERA_OFFLINE: 'Camera offline',
    CAMERA_RECOVERED: 'Camera recovered',
    SERVICE_FAILURE: 'Service failure',
    ALERT_STORM: 'Alert storm — digest mode engaged',
    CASHIER_ABSENT: 'Cashier absent from counter',
    CUSTOMER_WAIT_EXCEEDED: 'Customer waiting too long',
};
/**
 * Baseline severity per event type. A matching row in `alert_rules` overrides
 * this at runtime; this table is only the fallback when no rule is configured.
 */
export const DEFAULT_EVENT_SEVERITY = {
    PERSON_ENTERED: 'LOW',
    PERSON_EXITED: 'LOW',
    ZONE_OCCUPANCY_CHANGED: 'LOW',
    LINE_CROSSED: 'LOW',
    RESTRICTED_AREA_ENTRY: 'HIGH',
    AFTER_HOURS_MOVEMENT: 'HIGH',
    CROWD_THRESHOLD: 'MEDIUM',
    QUEUE_THRESHOLD: 'MEDIUM',
    STORAGE_ACTIVITY: 'HIGH',
    CAMERA_OFFLINE: 'HIGH',
    CAMERA_RECOVERED: 'LOW',
    SERVICE_FAILURE: 'CRITICAL',
    ALERT_STORM: 'HIGH',
    CASHIER_ABSENT: 'MEDIUM',
    CUSTOMER_WAIT_EXCEEDED: 'MEDIUM',
};
/**
 * Event types that are pure telemetry. They drive occupancy and analytics but
 * are never persisted individually and never become alerts — persisting one row
 * per person per frame would generate millions of worthless rows per day.
 */
export const TELEMETRY_EVENT_TYPES = [
    'PERSON_ENTERED',
    'PERSON_EXITED',
    'ZONE_OCCUPANCY_CHANGED',
    'LINE_CROSSED',
];
export function isTelemetryEvent(type) {
    return TELEMETRY_EVENT_TYPES.includes(type);
}
//# sourceMappingURL=events.js.map